import React, { useEffect, useMemo, useState } from "react";
import {
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Routes, Route, useNavigate, useParams } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import {
  fetchPreorderProducts,
  fetchPreorderProductById,
} from "../../api/preorder.api";
import ShareButton from "../../Components/ShareButton";
import "../pop/pop.css";
import "../pop/ProductDetail.css";
import PreOrderImage from "../../images/pre_order.jpeg";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const DEFAULT_MAX_PRICE = 6000;

const isLocalHost = (hostname = "") =>
  hostname === "127.0.0.1" ||
  hostname === "localhost" ||
  hostname === "0.0.0.0";

const toAbsoluteUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const fallbackBase =
    API_BASE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "");

  try {
    const parsed = new URL(raw, fallbackBase || undefined);

    if (API_BASE_URL && isLocalHost(parsed.hostname)) {
      const apiUrl = new URL(API_BASE_URL);
      parsed.protocol = apiUrl.protocol;
      parsed.host = apiUrl.host;
    }

    if (
      typeof window !== "undefined" &&
      window.location.protocol === "https:" &&
      parsed.protocol === "http:"
    ) {
      parsed.protocol = "https:";
    }

    return parsed.toString();
  } catch {
    if (!API_BASE_URL) return raw;
    return raw.startsWith("/") ? `${API_BASE_URL}${raw}` : `${API_BASE_URL}/${raw}`;
  }
};

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getMaxUnitLimit = (item = {}) => {
  const parsed = Number(item?.max_unit_per_user);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getPreorderLimitMessage = (item = {}) => {
  const limit = getMaxUnitLimit(item);
  if (!limit) return "This pre-order quantity cannot be increased further.";
  return `You can only add up to ${limit} unit${limit > 1 ? "s" : ""} of ${item?.name || "this pre-order"} per user.`;
};

const getPreorderRank = (item = {}) => {
  const dateValue =
    item.updated_at ||
    item.updatedAt ||
    item.created_at ||
    item.createdAt;
  const timestamp = Date.parse(dateValue);

  if (Number.isFinite(timestamp)) {
    return timestamp;
  }

  return toFiniteNumber(item.id);
};

const getBrandLabel = (item = {}) => {
  const rawBrand = String(item.brandLabel || item.category_name || item.brand || "").trim();
  const normalized = rawBrand.toLowerCase();

  if (
    !rawBrand ||
    ["none", "null", "undefined", "na", "n/a", "-", "—"].includes(normalized)
  ) {
    return "UNBRANDED";
  }

  return rawBrand;
};

const normalizePreorderProduct = (product = {}) => {
  const brandLabel = getBrandLabel(product);
  const brandKey = brandLabel.toLowerCase();
  const rowImages = Array.isArray(product.rows)
    ? [...product.rows]
        .filter((row) => row?.image && row.checked !== false)
        .sort(
          (a, b) =>
            Number(a?.order_number || 0) - Number(b?.order_number || 0)
        )
        .map((row) => toAbsoluteUrl(row.image))
        .filter(Boolean)
    : [];

  const fallbackImage = toAbsoluteUrl(product.image);
  const images = Array.from(
    new Set([fallbackImage, ...rowImages].filter(Boolean))
  );
  const depositAmount = toFiniteNumber(product.deposit_amount);
  const productTax = toFiniteNumber(product.product_tax);

  return {
    ...product,
    id: product.id,
    name: product.name ?? product.title ?? "",
    brand: brandKey,
    brandLabel,
    image: fallbackImage,
    images,
    price: depositAmount,
    deposit_amount: depositAmount,
    product_tax: productTax,
    total_due_now: depositAmount + productTax,
    mrp: 0,
    points: toFiniteNumber(product.points ?? product.Points),
    member_points: toFiniteNumber(
      product.member_points ?? product.memberPoints
    ),
    linked_product_id: product.linked_product_id ?? product.linked_product ?? null,
    linked_product_name: product.linked_product_name ?? "",
    member_only: Boolean(product.member_only),
    max_unit_per_user: getMaxUnitLimit(product),
    is_active: product.is_active !== false,
    status: String(product.status ?? "").trim(),
    start_date: product.start_date ?? "",
    end_date: product.end_date ?? "",
    details: product.description
      ? Array.isArray(product.description)
        ? product.description
        : [product.description]
      : [],
    is_new_arrival: false,
    is_trending: false,
  };
};

const resolveDisplayPoints = (item, isMember) => {
  const basePoints = toFiniteNumber(item?.points ?? item?.Points);
  const memberPoints = toFiniteNumber(
    item?.member_points ?? item?.memberPoints
  );

  if (isMember) {
    return memberPoints;
  }

  return basePoints;
};

const getCartTargetId = (item) =>
  item?.linked_product_id ?? item?.linked_product ?? item?.id ?? null;

const isPreorderPurchasable = (item = {}, isMember) => {
  if (!item?.is_active) return false;
  if (!getCartTargetId(item)) return false;

  const status = String(item?.status ?? "").trim().toLowerCase();
  if (status && status !== "ongoing") return false;

  return true;
};

const PreOrderListing = () => {
  const ITEMS_PER_PAGE = 20;
  const navigate = useNavigate();
  const { updateCart, getPreorderQuantity } = useCart();
  const { isAuthenticated, setShowLoginModal, isMember } = useAuth();

  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [sortBy, setSortBy] = useState("recommended");
  const [priceRange, setPriceRange] = useState([0, DEFAULT_MAX_PRICE]);
  const [tempPriceRange, setTempPriceRange] = useState([0, DEFAULT_MAX_PRICE]);
  const [activePage, setActivePage] = useState(1);
  const [limitDialog, setLimitDialog] = useState("");
  const hidePreorderPrice = isMember;

  useEffect(() => {
    let mounted = true;

    fetchPreorderProducts()
      .then((data) => {
        if (!mounted) return;

        const mapped = (Array.isArray(data) ? data : []).map(normalizePreorderProduct);
        const maxProductPrice = mapped.reduce(
          (max, product) => Math.max(max, toFiniteNumber(product.price)),
          DEFAULT_MAX_PRICE
        );
        const sliderMax = Math.max(
          DEFAULT_MAX_PRICE,
          Math.ceil(maxProductPrice / 100) * 100
        );

        const uniqueBrands = Array.from(
          mapped.reduce((brandMap, product) => {
            if (!product.brand) return brandMap;

            if (!brandMap.has(product.brand)) {
              brandMap.set(product.brand, {
                name: product.brand,
                label: product.brandLabel,
                count: 0,
              });
            }

            brandMap.get(product.brand).count += 1;
            return brandMap;
          }, new Map()).values()
        );

        setProducts(mapped);
        setBrands(uniqueBrands);
        setPriceRange([0, sliderMax]);
        setTempPriceRange([0, sliderMax]);
      })
      .catch(() => {
        if (!mounted) return;
        setProducts([]);
        setBrands([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setTempPriceRange(priceRange);
  }, [priceRange]);

  const sliderMaxPrice = Math.max(
    DEFAULT_MAX_PRICE,
    ...products
      .map((product) => toFiniteNumber(product.price))
      .filter((price) => price > 0)
  );

  const filteredProducts = products.filter((product) => {
    if (selectedBrand && product.brand !== selectedBrand) return false;
    if (product.price < priceRange[0] || product.price > priceRange[1]) return false;
    return true;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === "recommended") {
      return getPreorderRank(b) - getPreorderRank(a);
    }
    if (sortBy === "price-low") return a.price - b.price;
    if (sortBy === "price-high") return b.price - a.price;
    if (sortBy === "date-new") return getPreorderRank(b) - getPreorderRank(a);
    if (sortBy === "date-old") return getPreorderRank(a) - getPreorderRank(b);
    return 0;
  });

  const totalPages = Math.ceil(sortedProducts.length / ITEMS_PER_PAGE);
  const startIndex = (activePage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentProducts = sortedProducts.slice(startIndex, endIndex);
  const selectedBrandLabel =
    selectedBrand
      ? brands.find((brand) => brand.name === selectedBrand)?.label || "PRE-ORDER"
      : "PRE-ORDER";
  const breadcrumbBrandLabel = selectedBrand
    ? selectedBrandLabel
    : "All";

  const goToPage = (page) => {
    setActivePage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i);
    } else if (activePage <= 3) {
      for (let i = 1; i <= maxVisible; i += 1) pages.push(i);
    } else if (activePage >= totalPages - 2) {
      for (let i = totalPages - 4; i <= totalPages; i += 1) pages.push(i);
    } else {
      for (let i = activePage - 2; i <= activePage + 2; i += 1) pages.push(i);
    }

    return pages;
  };

  const handleAddToCart = (product) => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    const cartTargetId = getCartTargetId(product);
    if (!isPreorderPurchasable(product, isMember) || !cartTargetId) return;
    const currentQty = getPreorderQuantity(product.id);
    const maxLimit = getMaxUnitLimit(product);
    if (maxLimit && currentQty >= maxLimit) {
      setLimitDialog(getPreorderLimitMessage(product));
      return;
    }

    updateCart({
      type: "preorder",
      id: product.id,
      delta: 1,
    });
  };

  const handleBrandClick = (brandName) => {
    setSelectedBrand(brandName === "ALL" ? null : brandName);
    setSortBy("recommended");
    setActivePage(1);
  };

  const viewProduct = (product) => {
    navigate(`product/${product.id}`);
  };

  if (loading) {
    return (
      <div className="loading-wrap">
        <div className="loader" />
        <p>Loading pre-orders…</p>
      </div>
    );
  }

  return (
    <div className="page">
      {limitDialog ? (
        <div className="checkout-dialog-backdrop">
          <div className="checkout-dialog">
            <h3>Quantity Limit Reached</h3>
            <p>{limitDialog}</p>
            <div className="checkout-dialog-actions">
              <button
                className="checkout-btn-secondary"
                onClick={() => setLimitDialog("")}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div
        className="banner"
        style={{ backgroundImage: `url(${PreOrderImage})` }}
      >
        <div className="banner-container">
          <div className="banner-box">
            <div className="banner-text">
              <h1 className="banner-brand-title">{selectedBrandLabel.toUpperCase()}</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="breadcrumb11">
        <div className="page-container">
          <span onClick={() => navigate("/")}>Home</span>
          <span className="highlight"> / </span>{" "}
          <span>{breadcrumbBrandLabel}</span>
          <span className="highlight"> / </span>{" "}
          {sortedProducts.length}
        </div>
      </div>

      <div className="main-content">
        <div className="content-wrapper">
          <div className="sidebar">
            <div className="filter-box">
              <h3 className="filter-title">BRANDS</h3>
              <div className="filter-options">
                <div
                  className={`brand-nav-item ${selectedBrand === null ? "brand-nav-active" : ""}`}
                  onClick={() => handleBrandClick("ALL")}
                >
                  <span>ALL</span>
                  <span className="filter-count">{products.length}</span>
                </div>

                {brands.map((brand) => (
                  <div
                    key={brand.name}
                    className={`brand-nav-item ${selectedBrand === brand.name ? "brand-nav-active" : ""}`}
                    onClick={() => handleBrandClick(brand.name)}
                  >
                    <span>{brand.label}</span>
                    <span className="filter-count">{brand.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="filter-box">
              <h3 className="filter-title">PRICES</h3>
              <div className="price-range">
                <span className="price-label">Range:</span>
                ₹{priceRange[0]} - ₹{priceRange[1]}
              </div>

              <div className="dual-slider">
                <input
                  type="range"
                  min="0"
                  max={sliderMaxPrice}
                  step="10"
                  value={tempPriceRange[0]}
                  onChange={(e) => {
                    const value = Math.min(Number(e.target.value), tempPriceRange[1] - 100);
                    setTempPriceRange([value, tempPriceRange[1]]);
                  }}
                  onMouseUp={() => {
                    setPriceRange(tempPriceRange);
                    setActivePage(1);
                  }}
                  onTouchEnd={() => {
                    setPriceRange(tempPriceRange);
                    setActivePage(1);
                  }}
                  className="thumb thumb-left"
                />

                <input
                  type="range"
                  min="0"
                  max={sliderMaxPrice}
                  step="10"
                  value={tempPriceRange[1]}
                  onChange={(e) => {
                    const value = Math.max(Number(e.target.value), tempPriceRange[0] + 100);
                    setTempPriceRange([tempPriceRange[0], value]);
                  }}
                  onMouseUp={() => {
                    setPriceRange(tempPriceRange);
                    setActivePage(1);
                  }}
                  onTouchEnd={() => {
                    setPriceRange(tempPriceRange);
                    setActivePage(1);
                  }}
                  className="thumb thumb-right"
                />

                <div className="slider-track" />
                <div
                  className="slider-range"
                  style={{
                    left: `${(tempPriceRange[0] / sliderMaxPrice) * 100}%`,
                    right: `${100 - (tempPriceRange[1] / sliderMaxPrice) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>

          <div className="products-section">
            <div className="mobile-filter-bar">
              <span className="mobile-filter-label">Refine</span>
              <button
                className="mobile-filter-btn"
                onClick={() => setFilterOpen(true)}
              >
                Filters
              </button>
            </div>

            <div className="sort-bar">
              <div className="sort-label">Sort By:</div>
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setActivePage(1);
                }}
                className="sort-select"
              >
                <option value="recommended">Recommended</option>
                <option value="price-high">Price, High To Low</option>
                <option value="price-low">Price, Low To High</option>
                <option value="date-new">Date, New to Old</option>
                <option value="date-old">Date, Old to New</option>
              </select>
            </div>

            <div className="products-grid">
              {currentProducts.length > 0 ? (
                currentProducts.map((product) => {
                  const currentQty = getPreorderQuantity(product.id);
                  const isSoldOut = !isPreorderPurchasable(product, isMember);
                  const isInCart = currentQty > 0;
                  const displayPoints = resolveDisplayPoints(product, isMember);
                  const showPointsBadge = displayPoints > 0;
                  const handleIncreaseQty = () => {
                    const maxLimit = getMaxUnitLimit(product);
                    if (maxLimit && currentQty >= maxLimit) {
                      setLimitDialog(getPreorderLimitMessage(product));
                      return;
                    }
                    updateCart({
                      type: "preorder",
                      id: product.id,
                      delta: 1,
                    });
                  };

                  return (
                    <div key={product.id} className="product-card">
                      <div className="product-image-wrapper">
                        {product.status ? (
                          <div className="product-badge">
                            {product.status.toUpperCase()}
                          </div>
                        ) : null}

                        {showPointsBadge && (
                          <div className="product-points-badge">₹{displayPoints} Rewards</div>
                        )}

                        <img
                          src={product.image}
                          alt={product.name}
                          className="product-image"
                          onClick={() => viewProduct(product)}
                        />
                      </div>

                      <div className="product-info">
                        <h4 className="product-name" onClick={() => viewProduct(product)}>
                          {product.name}
                        </h4>

                        <div className="product-brand">
                          {product.brandLabel} {product.variant}
                        </div>

                        {!hidePreorderPrice && (
                          <>
                            <div className="product-pricing">
                              <span className="product-price">₹{product.deposit_amount}</span>
                            </div>

                            <div className="product-brand">
                              Deposit {product.product_tax > 0 ? `+ Tax ₹${product.product_tax}` : ""}
                            </div>
                          </>
                        )}

                        {product.member_only && (
                          <div className="product-brand">MEMBERS ONLY</div>
                        )}

                        <div className="product-actions">
                          {isSoldOut ? (
                            <button className="add-to-cart-btn" disabled>
                              {product.status ? product.status.toUpperCase() : "UNAVAILABLE"}
                            </button>
                          ) : !isInCart ? (
                            <button
                              className="add-to-cart-btn"
                              onClick={() => handleAddToCart(product)}
                            >
                              <ShoppingCart size={16} />
                              Add To Cart
                            </button>
                          ) : (
                            <div className="quantity-selector">
                              <button
                                className="quantity-btn"
                                onClick={() =>
                                  updateCart({
                                    type: "preorder",
                                    id: product.id,
                                    delta: -1,
                                  })
                                }
                              >
                                -
                              </button>

                              <span className="quantity-value">{currentQty}</span>

                              <button
                                className="quantity-btn"
                                onClick={handleIncreaseQty}
                              >
                                +
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-products">
                  <p>No products found matching your filters.</p>
                </div>
              )}
            </div>

            {sortedProducts.length > 0 && totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => activePage > 1 && goToPage(activePage - 1)}
                  disabled={activePage === 1}
                  style={{
                    opacity: activePage === 1 ? 0.5 : 1,
                    cursor: activePage === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  <ChevronLeft size={18} />
                </button>

                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`pagination-btn ${activePage === pageNum ? "pagination-active" : ""}`}
                    onClick={() => goToPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                ))}

                <button
                  className="pagination-btn"
                  onClick={() => activePage < totalPages && goToPage(activePage + 1)}
                  disabled={activePage === totalPages}
                  style={{
                    opacity: activePage === totalPages ? 0.5 : 1,
                    cursor: activePage === totalPages ? "not-allowed" : "pointer",
                  }}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}

            {sortedProducts.length > 0 && (
              <div
                style={{
                  textAlign: "center",
                  marginTop: "16px",
                  color: "#6b7280",
                  fontSize: "14px",
                }}
              >
                Showing {startIndex + 1} - {Math.min(endIndex, sortedProducts.length)} of{" "}
                {sortedProducts.length} products
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`mobile-filter-drawer ${filterOpen ? "open" : ""}`}>
        <div className="drawer-header">
          <span>Filters</span>
          <button onClick={() => setFilterOpen(false)}>✕</button>
        </div>

        <div className="drawer-body">
          <div className="sidebar">
            <div className="filter-box">
              <h3 className="filter-title">BRANDS</h3>
              <div className="filter-options">
                <div
                  className={`brand-nav-item ${selectedBrand === null ? "brand-nav-active" : ""}`}
                  onClick={() => handleBrandClick("ALL")}
                >
                  <span>ALL</span>
                  <span className="filter-count">{products.length}</span>
                </div>

                {brands.map((brand) => (
                  <div
                    key={brand.name}
                    className={`brand-nav-item ${selectedBrand === brand.name ? "brand-nav-active" : ""}`}
                    onClick={() => handleBrandClick(brand.name)}
                  >
                    <span>{brand.label}</span>
                    <span className="filter-count">{brand.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="filter-box">
              <h3 className="filter-title">PRICES</h3>
              <div className="price-range">
                <span className="price-label">Range:</span>
                ₹{priceRange[0]} - ₹{priceRange[1]}
              </div>

              <div className="dual-slider">
                <input
                  type="range"
                  min="0"
                  max={sliderMaxPrice}
                  step="100"
                  value={priceRange[0]}
                  onChange={(e) => {
                    const value = Math.min(Number(e.target.value), priceRange[1] - 100);
                    setPriceRange([value, priceRange[1]]);
                    setActivePage(1);
                  }}
                  className="thumb thumb-left"
                />

                <input
                  type="range"
                  min="0"
                  max={sliderMaxPrice}
                  step="100"
                  value={priceRange[1]}
                  onChange={(e) => {
                    const value = Math.max(Number(e.target.value), priceRange[0] + 100);
                    setPriceRange([priceRange[0], value]);
                    setActivePage(1);
                  }}
                  className="thumb thumb-right"
                />

                <div className="slider-track" />
                <div
                  className="slider-range"
                  style={{
                    left: `${(priceRange[0] / sliderMaxPrice) * 100}%`,
                    right: `${100 - (priceRange[1] / sliderMaxPrice) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`drawer-backdrop ${filterOpen ? "show" : ""}`}
        onClick={() => setFilterOpen(false)}
      />
    </div>
  );
};

const PreOrderDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { updateCart, getPreorderQuantity, isPreorderInCart } = useCart();
  const { isMember, isAuthenticated, setShowLoginModal } = useAuth();

  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [limitDialog, setLimitDialog] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        setLoading(true);

        const [productData, preorderList] = await Promise.all([
          fetchPreorderProductById(productId),
          fetchPreorderProducts(),
        ]);

        if (!mounted) return;

        const normalizedProduct = normalizePreorderProduct(productData);
        const normalizedList = (Array.isArray(preorderList) ? preorderList : [])
          .map(normalizePreorderProduct)
          .filter((product) => product.id !== normalizedProduct.id);

        const sameBrandProducts = normalizedList.filter(
          (product) => product.brand === normalizedProduct.brand
        );

        setSelectedProduct(normalizedProduct);
        setRelatedProducts(
          (sameBrandProducts.length ? sameBrandProducts : normalizedList).slice(0, 8)
        );
      } catch {
        if (!mounted) return;
        setSelectedProduct(null);
        setRelatedProducts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [productId]);

  const images = useMemo(() => {
    if (!selectedProduct) return [];
    return selectedProduct.images?.length
      ? selectedProduct.images
      : [selectedProduct.image].filter(Boolean);
  }, [selectedProduct]);

  const normalizedDetails = useMemo(() => {
    const details = Array.isArray(selectedProduct?.details)
      ? selectedProduct.details
      : [];

    return details.flatMap((detail) =>
      String(detail)
        .split(/\n|\./)
        .map((item) => item.trim())
        .filter(Boolean)
    );
  }, [selectedProduct]);

  const cartQty = selectedProduct ? getPreorderQuantity(selectedProduct.id) : 0;
  const isInCart = selectedProduct ? isPreorderInCart(selectedProduct.id) : false;
  const isSoldOut = !isPreorderPurchasable(selectedProduct, isMember);
  const selectedPoints = resolveDisplayPoints(selectedProduct, isMember);
  const showSelectedPointsBadge = selectedPoints > 0;
  const hidePreorderPrice = isMember;

  useEffect(() => {
    if (selectedProduct) setSelectedImage(0);
  }, [selectedProduct]);

  useEffect(() => {
    if (isAdding && (isInCart || cartQty > 0)) {
      setIsAdding(false);
    }
  }, [isAdding, isInCart, cartQty]);

  const handleAddToCart = (product = selectedProduct) => {
    if (!product) return;
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    const targetId = getCartTargetId(product);
    if (!isPreorderPurchasable(product, isMember) || !targetId) return;
    const currentQty = getPreorderQuantity(product.id);
    const maxLimit = getMaxUnitLimit(product);
    if (maxLimit && currentQty >= maxLimit) {
      setLimitDialog(getPreorderLimitMessage(product));
      return;
    }

    if (product.id === selectedProduct?.id) {
      setIsAdding(true);
    }

    updateCart({
      type: "preorder",
      id: product.id,
      delta: 1,
    });
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading-wrap">
          <div className="loader" />
          <div>Loading pre-order product...</div>
        </div>
      </div>
    );
  }

  if (!selectedProduct) {
    return (
      <div className="page">
        <div className="loading-wrap">
          <div>Pre-order product not found</div>
        </div>
      </div>
    );
  }

  const detailBreadcrumbLabel =
    selectedProduct.brandLabel ||
    selectedProduct.category_name ||
    "All";

  return (
    <div className="page">
      {limitDialog ? (
        <div className="checkout-dialog-backdrop">
          <div className="checkout-dialog">
            <h3>Quantity Limit Reached</h3>
            <p>{limitDialog}</p>
            <div className="checkout-dialog-actions">
              <button
                className="checkout-btn-secondary"
                onClick={() => setLimitDialog("")}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="breadcrumb11">
        <div className="page-container">
          <span onClick={() => navigate("/")}>Home</span>
          <span className="highlight"> / </span>
          <span
            className="breadcrumb-link"
            onClick={() => navigate("/pre-order")}
          >
            {detailBreadcrumbLabel}
          </span>
          <span className="highlight"> / </span>
          {selectedProduct.name}
        </div>
      </div>

      <div className="detail-content">
        <div className="detail-grid">
          <div className="pd-media">
            <div className="pd-thumbnails">
              {images.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`thumb-${idx}`}
                  className={selectedImage === idx ? "active" : ""}
                  onClick={() => setSelectedImage(idx)}
                />
              ))}
            </div>

            <div className="pd-main-image">
              <img
                src={images[selectedImage] || selectedProduct.image}
                alt={selectedProduct.name}
              />
            </div>
          </div>

          <div className="pd-info">
            <div className="pd-brand">{selectedProduct.brandLabel}</div>
            <h1 className="pd-title">{selectedProduct.name}</h1>

            {!hidePreorderPrice && (
              <div className="pd-price-row">
                <span className="pd-price">₹{selectedProduct.deposit_amount}</span>
              </div>
            )}

            <div className="pd-details">
              <h3>Pre-Order Details</h3>
              <ul>
                <li>Category: {selectedProduct.brandLabel}</li>
                <li>Status: {selectedProduct.status || "Upcoming"}</li>
                {!hidePreorderPrice ? (
                  <>
                    <li>Deposit Amount: ₹{selectedProduct.deposit_amount}</li>
                    <li>Tax: ₹{selectedProduct.product_tax}</li>
                    <li>Due Now: ₹{selectedProduct.total_due_now}</li>
                  </>
                ) : null}
                {selectedProduct.linked_product_name ? (
                  <li>Linked Product: {selectedProduct.linked_product_name}</li>
                ) : null}
                {selectedProduct.start_date ? (
                  <li>Starts: {new Date(selectedProduct.start_date).toLocaleString()}</li>
                ) : null}
                {selectedProduct.end_date ? (
                  <li>Ends: {new Date(selectedProduct.end_date).toLocaleString()}</li>
                ) : null}
                {selectedProduct.member_only ? <li>Members Only</li> : null}
              </ul>
            </div>

            {showSelectedPointsBadge && (
              <div className="pd-points">
                Earn ₹{selectedPoints} Rewards on this purchase
              </div>
            )}

            <div className="pd-actions">
              {isInCart && !isSoldOut && (
                <div className="pd-qty">
                  <button
                    onClick={() =>
                      updateCart({
                        type: "preorder",
                        id: selectedProduct.id,
                        delta: -1,
                      })
                    }
                  >
                    -
                  </button>
                  <span>{cartQty}</span>
                  <button
                    onClick={() => {
                      const maxLimit = getMaxUnitLimit(selectedProduct);
                      if (maxLimit && cartQty >= maxLimit) {
                        setLimitDialog(getPreorderLimitMessage(selectedProduct));
                        return;
                      }
                      updateCart({
                        type: "preorder",
                        id: selectedProduct.id,
                        delta: 1,
                      });
                    }}
                  >
                    +
                  </button>
                </div>
              )}

              {!isSoldOut && !isInCart ? (
                <button
                  className="pd-add-cart"
                  onClick={() => handleAddToCart()}
                  disabled={isAdding}
                >
                  {isAdding ? <span className="btn-spinner" /> : "Add To Cart"}
                </button>
              ) : isSoldOut ? (
                <button className="pd-soldout" disabled>
                  {selectedProduct.status ? selectedProduct.status.toUpperCase() : "UNAVAILABLE"}
                </button>
              ) : null}

              <ShareButton
                title={selectedProduct.name}
                text={`Check out ${selectedProduct.name} on DiecastZone`}
              />
            </div>

            <div className="pd-details">
              <h3>Product Details</h3>
              <ul>
                {normalizedDetails.map((detail, index) => (
                  <li key={index}>{detail}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="related-section">
          <div className="container">
            <div className="related-header">
              <h2 className="related-title">
                RELATED <span className="related-accent">PRE-ORDERS</span>
              </h2>
            </div>

            <div className="related-grid">
              {relatedProducts.length > 0 ? (
                relatedProducts.map((product) => {
                  const price = product.price;
                  const displayPoints = resolveDisplayPoints(product, isMember);
                  const currentQty = getPreorderQuantity(product.id);
                  const productInCart = isPreorderInCart(product.id);
                  const productSoldOut = !isPreorderPurchasable(product, isMember);
                  const handleIncreaseRelatedQty = () => {
                    const maxLimit = getMaxUnitLimit(product);
                    if (maxLimit && currentQty >= maxLimit) {
                      setLimitDialog(getPreorderLimitMessage(product));
                      return;
                    }
                    updateCart({
                      type: "preorder",
                      id: product.id,
                      delta: 1,
                    });
                  };

                  return (
                    <div key={product.id} className="related-card">
                      <div className="related-image-wrapper">
                        {product.status ? (
                          <div className="related-badge">{product.status}</div>
                        ) : null}

                        {displayPoints > 0 && (
                          <div className="related-points-badge">₹{displayPoints} Rewards</div>
                        )}

                        <img
                          src={product.image}
                          alt={product.name}
                          className="related-image"
                          onClick={() => navigate(`../product/${product.id}`)}
                        />
                      </div>

                      <div className="related-info">
                        <h3
                          className="related-name"
                          onClick={() => navigate(`../product/${product.id}`)}
                        >
                          {product.name}
                        </h3>

                        <div className="related-brand">
                          {product.brandLabel} {product.variant}
                        </div>

                        {!hidePreorderPrice && (
                          <>
                            <div className="related-price-row">
                              <div className="related-price">₹{price}</div>
                            </div>

                            <div className="related-brand">
                              Deposit {product.product_tax > 0 ? `+ Tax ₹${product.product_tax}` : ""}
                            </div>
                          </>
                        )}

                        <div className="related-action-row">
                          {productSoldOut ? (
                            <button className="related-cart-btn related-cart-btn-disabled" disabled>
                              {product.status || "Unavailable"}
                            </button>
                          ) : !productInCart ? (
                            <button
                              onClick={() => handleAddToCart(product)}
                              className="related-cart-btn"
                            >
                              <ShoppingCart size={16} />
                              Add To Cart
                            </button>
                          ) : (
                            <div className="related-qty">
                              <button
                                onClick={() =>
                                  updateCart({
                                    type: "preorder",
                                    id: product.id,
                                    delta: -1,
                                  })
                                }
                              >
                                -
                              </button>
                              <span>{currentQty}</span>
                              <button
                                onClick={handleIncreaseRelatedQty}
                              >
                                +
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="related-empty-state">
                  <div className="related-empty-icon">PRE</div>
                  <h3>No related pre-orders found</h3>
                  <p>
                    More upcoming launches will appear here once matching pre-orders are available.
                  </p>
                  <button
                    type="button"
                    className="related-empty-btn"
                    onClick={() => navigate("/pre-order")}
                  >
                    Browse All Pre-Orders
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PreOrderPage = () => (
  <Routes>
    <Route index element={<PreOrderListing />} />
    <Route path="product/:productId" element={<PreOrderDetail />} />
  </Routes>
);

export default PreOrderPage;
